import React, { useState, useEffect } from 'react';
import { useWizardFormContext } from 'providers/WizardFormProvider';
import { Form, Table, Button, FormControlProps } from 'react-bootstrap';
import { WizardFormData } from 'components/forms/CampaignWizardForm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  fetchBXPOutboundRules,
  fetchBXPCampaignOutboundRules
} from 'services/hvaservicecampaign.service';

interface OutboundRule {
  id: number;
  shortname: string;
}

interface CampaignOutboundRuleAssociation {
  id?: number;
  campaign_id: number;
  outrule_id: number;
  limitcall: number;
  weight: number;
  dialpattern: string;
}

interface WizardOutboundRulesFormProps {
  campaignId?: number;
}

const WizardOutboundRulesForm: React.FC<WizardOutboundRulesFormProps> = ({
  campaignId
}) => {
  const { formData, onChange } = useWizardFormContext<WizardFormData>();
  const [outboundRules, setOutboundRules] = useState<OutboundRule[]>([]);
  // eslint-disable-next-line
  const [associations, setAssociations] = useState<CampaignOutboundRuleAssociation[]
  >(formData.outboundRuleAssociations || []);
  const [newAssociation, setNewAssociation] =
    useState<CampaignOutboundRuleAssociation>({
      campaign_id: campaignId || 0,
      outrule_id: 0,
      limitcall: 0,
      weight: 0,
      dialpattern: ''
    });

  useEffect(() => {
    const loadData = async () => {
      try {
        const rules = await fetchBXPOutboundRules();
        setOutboundRules(rules);

        if (campaignId && !formData.outboundRuleAssociations) {
          // eslint-disable-next-line
          const campaignAssociations = await fetchBXPCampaignOutboundRules(campaignId);
          setAssociations(campaignAssociations);
          // eslint-disable-next-line
          onChange({ target: { name: 'outboundRuleAssociations', value: campaignAssociations } } as any);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, [campaignId, formData.outboundRuleAssociations, onChange]);

  const handleAddAssociation = () => {
    if (newAssociation.outrule_id === 0) {
      // Si no se ha seleccionado una Outbound Rule, no hacemos nada
      return;
    }

    const updatedAssociations = [
      ...associations,
      { ...newAssociation, id: Date.now() }
    ];
    setAssociations(updatedAssociations);
    // eslint-disable-next-line
    onChange({ target: { name: 'outboundRuleAssociations', value: updatedAssociations } } as any);
    setNewAssociation({
      ...newAssociation,
      outrule_id: 0,
      limitcall: 0,
      weight: 0,
      dialpattern: ''
    });
  };

  const handleRemoveAssociation = (id: number) => {
    const updatedAssociations = associations.filter(a => a.id !== id);
    setAssociations(updatedAssociations);
    // eslint-disable-next-line
    onChange({ target: { name: 'outboundRuleAssociations', value: updatedAssociations } } as any);
  };

  const handleInputChange: FormControlProps['onChange'] = e => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const { name, value } = target;
    setNewAssociation(prev => ({
      ...prev,
      [name]:
        name === 'outrule_id' || name === 'limitcall' || name === 'weight'
          ? Number(value)
          : value
    }));
  };

  const isAddButtonDisabled = newAssociation.outrule_id === 0;

  return (
    <>
      <p className="fs-9 text-primary">
        These setting are only aplicable to Outbound Campaigns!
      </p>
      <Form.Group className="mb-3">
        <Form.Label>Caller Number</Form.Label>
        <Form.Control
          type="text"
          name="callernumber"
          placeholder="Enter Caller Number"
          value={formData.callernumber || ''}
          onChange={onChange}
          maxLength={32}
        />
        <Form.Control.Feedback type="invalid">
          Caller Number is required.
        </Form.Control.Feedback>
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Check
          type="switch"
          id="amd"
          label="Automatic Mail Detection (AMD)"
          name="amd"
          checked={formData.amd === true}
          onChange={onChange}
        />
      </Form.Group>

      <div className="border rounded p-0 bg-body">
        <Table className="phoenix-table">
          <thead className="fs-10 pt-2 pb-1 border_bottom">
            <tr>
              <th className="px-5 pb-1 fs-10 fw-bold text-body-tertiary">
                Outbound_Rule
              </th>
              <th className="px-4 pb-1 fs-10 fw-bold text-body-tertiary">
                Limit
              </th>
              <th className="px-4 pb-1 fs-10 fw-bold text-body-tertiary">
                Weight
              </th>
              <th className="px-4 pb-1 fs-10 fw-bold text-body-tertiary">
                Dial Pattern
              </th>
              <th className="px-5 pb-1 fs-11">·</th>
            </tr>
          </thead>
          <tbody className="fs-10 pt-2 pb-1">
            {associations.map(association => (
              <tr key={association.id} className="bg-body-emphasis">
                <td className="px-5 pt-1 pb-1 fs-9">
                  {
                    outboundRules.find(r => r.id === association.outrule_id)
                      ?.shortname
                  }
                </td>
                <td className="px-4 pt-1 pb-1 fs-9">{association.limitcall}</td>
                <td className="px-4 pt-1 pb-1 fs-9">{association.weight}</td>
                <td className="px-4 pt-1 pb-1 fs-9">
                  {association.dialpattern}
                </td>
                <td className="pe-2 pt-1 pb-1 fs-9">
                  <Button
                    variant="primary"
                    size="sm"
                    // eslint-disable-next-line
                    onClick={() => association.id && handleRemoveAssociation(association.id)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </td>
              </tr>
            ))}
            <tr className="border-0">
              <td className="px-3 border-0">
                <Form.Control
                  as="select"
                  name="outrule_id"
                  value={newAssociation.outrule_id}
                  onChange={handleInputChange}
                >
                  <option value="">Select an outbound rule</option>
                  {outboundRules.map(rule => (
                    <option key={rule.id} value={rule.id}>
                      {rule.shortname}
                    </option>
                  ))}
                </Form.Control>
              </td>
              <td className="border-0">
                <Form.Control
                  type="number"
                  name="limitcall"
                  value={newAssociation.limitcall}
                  onChange={handleInputChange}
                  placeholder="Limit"
                />
              </td>
              <td className="border-0">
                <Form.Control
                  type="number"
                  name="weight"
                  value={newAssociation.weight}
                  onChange={handleInputChange}
                  placeholder="Weight"
                />
              </td>
              <td className="border-0">
                <Form.Control
                  type="text"
                  name="dialpattern"
                  value={newAssociation.dialpattern}
                  onChange={handleInputChange}
                  placeholder="Dial Pattern"
                />
              </td>
              <td className="border-0">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleAddAssociation}
                  disabled={isAddButtonDisabled}
                >
                  <FontAwesomeIcon icon={faPlus} />
                </Button>
              </td>
            </tr>
          </tbody>
        </Table>
      </div>
    </>
  );
};

export default WizardOutboundRulesForm;
